import {
  AnchorButton,
  Button,
  Callout,
  Classes,
  Collapse,
  Divider,
  Drawer,
  H5,
  Intent,
  Position,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import { createSelector } from '@reduxjs/toolkit';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { layoutAlgorithmSetup } from 'src/containers/ProjectDiagram/templates/extensions/layouts/LayoutAlgorithm';
import { LayoutAlgorithmType } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';
import { redrawLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/utils/redrawLayout';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setAllPropertyTypes } from 'src/containers/ProjectDiagram/utils/propertyType/setAllPropertyTypes';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { projectSelector } from 'src/selectors/project';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { propertyTypeCountSelector } from 'src/selectors/propertyType';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  INVALID_IRI_MATCHER,
  IRI_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidIriChars';

const validateName = (name: string) =>
  name ? '' : copy.components.projectSettingsDrawer.validation.nameRequired;

const validateIri = (iri: string): string => {
  if (!iri) {
    return copy.components.projectSettingsDrawer.validation.namespaceRequired;
  }

  if (!iri.match(IRI_STARTS_WITH_MATCHER)) {
    return copy.components.projectSettingsDrawer.validation.namespaceStartsWith;
  }

  if (!iri.includes(':')) {
    return copy.components.projectSettingsDrawer.validation.namespaceSemicolon;
  }

  const invalidChars = iri.match(INVALID_IRI_MATCHER);
  if (invalidChars) {
    return copy.components.projectSettingsDrawer.validation.namespaceChars(
      uniq(invalidChars)
    );
  }

  return '';
};

const namespacesByModelIdSelector = (projectId: string) =>
  createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
    Object.values(modelsById).reduce<Record<string, string>>(
      (acc, model) => ({
        ...acc,
        [model.id]: model.namespace,
      }),
      {}
    )
  );

// warning should be visible when custom property types will be overwritten
// (when going from custom to yes/no and custom values don't match selection)
export const isPropertyTypeWarningVisible = (
  projectPropertyType: ProjectPropertyType | undefined,
  newPropertyType: ProjectPropertyType,
  isAllPropertyType: ReturnType<
    ReturnType<typeof propertyTypeCountSelector>
  >['isAllPropertyType']
) =>
  newPropertyType !== ProjectPropertyType.CUSTOM &&
  projectPropertyType === ProjectPropertyType.CUSTOM &&
  !isAllPropertyType[newPropertyType];

const ProjectSettingsDrawerBody = () => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const projectModels = useReduxSelector(projectModelsByIdSelector(projectId));
  const initialNamespacesByModelId = useReduxSelector(
    namespacesByModelIdSelector(projectId)
  );

  const { isAllPropertyType } = useReduxSelector(
    propertyTypeCountSelector(projectId)
  );

  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const [name, setName] = React.useState(project?.name || '');
  const [description, setDescription] = React.useState(
    project?.description || ''
  );
  const [modelNamespaces, setModelNamespaces] = React.useState(
    initialNamespacesByModelId
  );
  const [dataNamespace, setDataNamespace] = React.useState(
    project?.dataNamespace || ''
  );
  const [propertyType, setPropertyType] = React.useState<ProjectPropertyType>(
    project?.propertyType || DEFAULT_PROJECT_PROPERTY_TYPE
  );
  const [projectLayout, setProjectLayout] = React.useState(
    diagram?.model.modelData.layoutAlgorithm ||
      LayoutAlgorithmType.D3_FORCE_LAYOUT
  );
  const modelsNotInDiagram = Object.values(projectModels).filter(
    (model) => !diagram?.model.modelData.deletedModelIds?.includes(model.id)
  );
  const [validation, setValidation] = React.useState({
    name: true,
    namespace: true,
    validateIri: modelsNotInDiagram.map(() => true),
  });

  const withValidation = (
    name: keyof typeof validation,
    handler: (value: string) => string,
    index = 0
  ) => {
    return (value: string) => {
      const error = handler(value);
      const isValid = error === '';

      if (name === 'validateIri') {
        if (validation.validateIri[index] !== isValid) {
          setValidation((prev) => ({
            ...prev,
            validateIri: prev.validateIri.map((item, i) =>
              i === index ? isValid : item
            ),
          }));
        }
      } else if (validation[name] !== isValid) {
        setValidation((prev) => ({
          ...prev,
          [name]: isValid,
        }));
      }

      return error;
    };
  };

  React.useEffect(() => {
    setName(project?.name || '');
    setDescription(project?.description || '');
    setDataNamespace(project?.dataNamespace || '');
  }, [projectId, project]);

  const setModelNamespace = React.useCallback(
    (modelId: string, newNamespace: string) => {
      const newModelNamespaces = {
        ...modelNamespaces,
        [modelId]: newNamespace,
      };
      setModelNamespaces(newModelNamespaces);
    },
    [modelNamespaces]
  );

  const onClose = () => {
    setName('');
    setDescription('');
    setModelNamespaces({});
    setDataNamespace('');
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: false }));
  };

  const onSubmit = async () => {
    await dispatch(async (wrappedDispatch) => {
      wrappedDispatch(
        projectsActionCreators.clearedProjectVoiceboxEvaluation({
          id: projectId,
        })
      );

      if (project?.description !== description) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectDescription(
            projectId,
            description
          )
        );
      }

      if (project?.dataNamespace !== dataNamespace) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectDataNamespace(
            projectId,
            dataNamespace
          )
        );
      }

      if (project?.propertyType !== propertyType) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectPropertyType(
            projectId,
            propertyType
          )
        );

        if (diagram && propertyType !== ProjectPropertyType.CUSTOM) {
          // overwrite custom properties
          commitDiagramModel(diagram, setAllPropertyTypes(propertyType));

          // clear undo history because project settings can't be undone
          diagram.undoManager.clear();
        }
      }

      Object.entries(modelNamespaces).forEach(([modelId, modelNamespace]) => {
        if (projectModels[modelId]?.namespace !== modelNamespace) {
          wrappedDispatch(
            projectsActionCreators.updatedProjectModelNamespace(
              projectId,
              modelId,
              modelNamespace
            )
          );
        }
      });

      if (
        diagram &&
        diagram.model.modelData.layoutAlgorithm !== projectLayout
      ) {
        commitDiagramModel(diagram, (model) => {
          setDataProperty<ModelData>(model)(
            model.modelData,
            'layoutAlgorithm',
            projectLayout
          );
        });

        // having these in the same commit didn't run the layout
        commitDiagramModel(
          diagram,
          (model, diagram) => {
            // eslint-disable-next-line no-param-reassign
            diagram.layout = layoutAlgorithmSetup(
              model.modelData.layoutAlgorithm
            );

            redrawLayout(model, diagram);
          },
          { layout: DiagramModelCommitLayout.FULL }
        );

        // clear undo history because project settings can't be undone
        diagram.undoManager.clear();
      }
    });

    if (project?.name !== name) {
      // must wait for previous action middleware to finish completely
      // before renaming project to avoid cloud sync issues
      dispatch(projectsActionCreators.renamedProject({ id: projectId, name }));
    }

    onClose();
  };

  const isValid =
    validation.name &&
    validation.namespace &&
    validation.validateIri.every((item) => item);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.projectSettingsDrawer.container}
      >
        <InlineEditableLabel
          label={copy.components.projectSettingsDrawer.name}
          monospaceText={false}
          onSetValue={setName}
          onValidate={withValidation('name', validateName)}
          placeholder={project?.name || ''}
          testId={TEST_IDS.projectSettingsDrawer.name}
          value={name}
        />
        <InlineEditableLabel
          label={copy.components.projectSettingsDrawer.description}
          minLines={3}
          monospaceText={false}
          multiline
          onSetValue={setDescription}
          placeholder={project?.description || ''}
          testId={TEST_IDS.projectSettingsDrawer.description}
          value={description}
        />
        <RadioGroup
          className="sd-drawer-project-settings__radio-group"
          inline
          label={copy.components.projectSettingsDrawer.layout.layoutLabel}
          onChange={({ currentTarget: { value } }) => {
            setProjectLayout(value as LayoutAlgorithmType);
          }}
          selectedValue={projectLayout}
        >
          {Object.values(LayoutAlgorithmType)
            .sort(
              alphaSort(
                (algorithm) =>
                  copy.components.projectSettingsDrawer.layout[algorithm]
              )
            )
            .map((algorithm) => (
              <Radio
                key={algorithm}
                checked={projectLayout === algorithm}
                label={copy.components.projectSettingsDrawer.layout[algorithm]}
                value={algorithm}
              />
            ))}
        </RadioGroup>

        <Divider />
        <div className="sd-advanced-project-details">
          <InlineEditableLabel
            label={copy.components.projectSettingsDrawer.dataNamespace}
            onSetValue={setDataNamespace}
            onValidate={withValidation('namespace', validateIri)}
            placeholder={project?.dataNamespace || ''}
            testId={TEST_IDS.projectSettingsDrawer.dataNamespace}
            value={dataNamespace}
          />
          <PropertyTypeSelector
            allowCustom
            propertyType={propertyType}
            setPropertyType={setPropertyType}
          />
          <Collapse
            isOpen={isPropertyTypeWarningVisible(
              project?.propertyType,
              propertyType,
              isAllPropertyType
            )}
          >
            <Callout
              data-testid={
                TEST_IDS.projectSettingsDrawer.customPropertyTypeWarning
              }
              intent={Intent.WARNING}
            >
              {copy.components.projectSettingsDrawer.customPropertyTypeWarning}
            </Callout>
          </Collapse>
        </div>
        <>
          <Divider />
          <H5>{copy.components.projectSettingsDrawer.modelSettings}</H5>
          {modelsNotInDiagram
            .sort(alphaSort((model) => model.name))
            .map((model, idx) => {
              return (
                <InlineEditableLabel
                  key={model.id}
                  label={copy.components.projectSettingsDrawer.namespace(
                    model.name
                  )}
                  onSetValue={(newNamespace) => {
                    setModelNamespace(model.id, newNamespace);
                  }}
                  onValidate={withValidation('validateIri', validateIri, idx)}
                  placeholder={model.namespace || ''}
                  testId={TEST_IDS.projectSettingsDrawer.modelNamespace(
                    model.id,
                    idx
                  )}
                  value={modelNamespaces[model.id] || ''}
                />
              );
            })}
        </>
        <Divider />
      </div>
      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectSettingsDrawer.cancelButton}
            intent={Intent.PRIMARY}
            onClick={onClose}
            outlined
            text={copy.components.projectSettingsDrawer.cancel}
          />
          <AnchorButton
            data-testid={TEST_IDS.projectSettingsDrawer.submitButton}
            disabled={!isValid}
            intent={Intent.PRIMARY}
            onClick={onSubmit}
            text={copy.components.projectSettingsDrawer.submit}
          />
        </div>
      </div>
    </>
  );
};

export const ProjectSettingsDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectSettingsDrawerOpen
  );

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: false }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-project-settings"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.projectSettingsDrawer.title}
    >
      <ProjectSettingsDrawerBody />
    </Drawer>
  );
};
